import { useMemo } from 'react'

import { useAppForm } from '@/components/form/form'
import { AdvancedFilterDrawer } from '@/components/table/advanced-filter-drawer'
import { useI18n } from '@/i18n/i18n-react'

// 'none' is the sentinel for "unassigned" (a member that belongs to no group).
export const UNASSIGNED_GROUP_FILTER = 'none'

export type GroupMemberFilters = {
	groups: string[]
}

type GroupOption = {
	uuid: string
	name: string
}

type GroupMembersAdvancedFilterDrawerProps = {
	open: boolean
	onOpenChange: (open: boolean) => void
	groups: GroupOption[]
	defaultValues: GroupMemberFilters
	onApply: (filters: GroupMemberFilters) => void
	labels: {
		groupLabel: string
		unassignedLabel: string
	}
}

export function GroupMembersAdvancedFilterDrawer({
	open,
	onOpenChange,
	groups,
	defaultValues,
	onApply,
	labels
}: GroupMembersAdvancedFilterDrawerProps) {
	const { L } = useI18n()

	const groupOptions = useMemo(
		() => [
			{ value: UNASSIGNED_GROUP_FILTER, label: labels.unassignedLabel },
			...groups.map(group => ({ value: group.uuid, label: group.name }))
		],
		[groups, labels.unassignedLabel]
	)

	const form = useAppForm({
		defaultValues,
		onSubmit: async ({ value }) => {
			onApply(value)
			onOpenChange(false)
		}
	})

	return (
		<form.AppForm>
			<AdvancedFilterDrawer
				open={open}
				onOpenChange={onOpenChange}
				onClearAll={() => {
					form.setFieldValue('groups', [])
					onApply({ groups: [] })
					onOpenChange(false)
				}}
				onApplyClick={() => form.handleSubmit()}
				title={L.General.AdvancedFilters()}
			>
				<form.AppField name='groups'>
					{field => <field.ComboboxField label={labels.groupLabel} options={groupOptions} optional />}
				</form.AppField>
			</AdvancedFilterDrawer>
		</form.AppForm>
	)
}
